import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, FunnelChart, Funnel, LabelList } from 'recharts';
import { Download, Filter, FileText, FileSpreadsheet, FileJson } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, StatCard } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { reportsApi } from '@/api/reports';
import { formatNumber } from '@/utils/formatters';
import toast from 'react-hot-toast';

const SOURCE_COLORS = ['#1877F2', '#EA4335', '#10B981', '#F59E0B', '#8B5CF6', '#14B8A6', '#F97316', '#06B6D4', '#94A3B8'];

export function ReportsPage() {
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', city: '', category: '', source: '' });
  const [exporting, setExporting] = useState<string | null>(null);

  const { data: report, isLoading, refetch } = useQuery({
    queryKey: ['reports', 'leads', filters],
    queryFn: () => reportsApi.getLeadsReport(filters),
  });

  const handleExport = async (type: 'excel' | 'pdf' | 'csv') => {
    setExporting(type);
    try {
      if (type === 'excel') {
        const blob = await reportsApi.exportExcel(filters);
        downloadBlob(blob, `report-${Date.now()}.xlsx`);
      } else if (type === 'pdf') {
        const blob = await reportsApi.exportPDF(filters);
        downloadBlob(blob, `report-${Date.now()}.pdf`);
      } else {
        const csv = await reportsApi.exportCSV(filters);
        const blob = new Blob([csv], { type: 'text/csv' });
        downloadBlob(blob, `report-${Date.now()}.csv`);
      }
      toast.success(`${type.toUpperCase()} exported successfully`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const sourceData = report?.bySource.map((s, i) => ({
    name: (s.source ?? 'Unknown').toUpperCase(),
    value: Number(s.count),
    color: SOURCE_COLORS[i % SOURCE_COLORS.length],
  })) ?? [];

  const bdData = report?.byBd.map(b => ({
    name: b.bdName ?? 'Unassigned',
    count: Number(b.count),
  })) ?? [];

  const cityData = report?.byCity.slice(0, 10).map(c => ({
    name: c.city ?? 'Unknown',
    count: Number(c.count),
  })) ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-500 text-sm mt-0.5">Comprehensive lead performance insights</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<FileSpreadsheet size={14} />}
            onClick={() => handleExport('excel')}
            loading={exporting === 'excel'}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<FileText size={14} />}
            onClick={() => handleExport('pdf')}
            loading={exporting === 'pdf'}
          >
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<Download size={14} />}
            onClick={() => handleExport('csv')}
            loading={exporting === 'csv'}
          >
            CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <Input
            label="From Date"
            type="date"
            value={filters.dateFrom}
            onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
          />
          <Input
            label="To Date"
            type="date"
            value={filters.dateTo}
            onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
          />
          <Input
            label="City"
            value={filters.city}
            onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
            placeholder="Filter by city"
          />
          <Input
            label="Category"
            value={filters.category}
            onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
            placeholder="Filter by category"
          />
          <Button icon={<Filter size={14} />} onClick={() => refetch()} loading={isLoading}>
            Apply Filters
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Leads', value: report?.summary.total ?? 0, color: 'blue' as const },
              { label: 'OPD Done', value: report?.summary.opd_done ?? 0, color: 'green' as const },
              { label: 'IPD Done', value: report?.summary.ipd_done ?? 0, color: 'purple' as const },
              { label: 'Insurance', value: report?.summary.insurance ?? 0, color: 'orange' as const },
              { label: 'EMI Cases', value: report?.summary.emi ?? 0, color: 'yellow' as const },
              { label: 'Closed Won', value: report?.summary.closed_won ?? 0, color: 'green' as const },
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl shadow-card border border-slate-100 p-4 text-center">
                <div className="text-2xl font-bold text-slate-900">{formatNumber(item.value)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{item.label}</div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Sources Pie */}
            <Card title="Leads by Source">
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={sourceData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2} dataKey="value">
                      {sourceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => [formatNumber(v), 'Leads']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5">
                  {sourceData.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
                        <span className="text-slate-600">{s.name}</span>
                      </div>
                      <span className="font-semibold text-slate-900">{formatNumber(s.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Conversion Funnel */}
            <Card title="Conversion Funnel">
              <div className="space-y-3">
                {report?.funnel.map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">{item.stage}</span>
                      <span className="font-semibold">{formatNumber(item.count)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full">
                      <div
                        className="h-full rounded-full bg-brand-cyan transition-all duration-700"
                        style={{ width: `${report.funnel[0].count ? (item.count / report.funnel[0].count) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* BD Performance */}
            <Card title="BD Performance">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={bdData} layout="vertical" margin={{ left: 60 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={60} />
                  <Tooltip formatter={(v: number) => [formatNumber(v), 'Leads']} />
                  <Bar dataKey="count" fill="#06B6D4" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* City Performance */}
            <Card title="Leads by City">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={cityData} margin={{ bottom: 40 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => [formatNumber(v), 'Leads']} />
                  <Bar dataKey="count" fill="#1E3A5F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Top Diseases Drilldown Table */}
          <Card title="Top Diseases">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">#</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Disease</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Leads</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {report?.byDisease.slice(0, 10).map((d, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-400 font-mono text-xs">{i + 1}</td>
                    <td className="px-4 py-2 text-slate-900">{d.disease ?? 'Not specified'}</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-900">{formatNumber(Number(d.count))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
